import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import ConceptGenerator from './components/ConceptGenerator';
import IdeaGallery from './components/IdeaGallery';
import { AppConcept } from './types';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { loadUserConcepts, saveConcept, migrateLocalStorageToFirebase } from './services/firebaseService';

function AppContent() {
  const [generatedConcepts, setGeneratedConcepts] = useState<AppConcept[]>([]);
  const { currentUser } = useAuth();

  // Load concepts from localStorage on initial load (before Firebase)
  useEffect(() => {
    const loadInitialConcepts = () => {
      const saved = localStorage.getItem('showerThoughtsConcepts');
      if (saved) {
        try {
          const localConcepts = JSON.parse(saved);
          console.log('🔄 Initial load: Found concepts in localStorage:', localConcepts.length);
          setGeneratedConcepts(localConcepts);
        } catch (parseError) {
          console.error('❌ Error parsing initial localStorage data:', parseError);
        }
      }
    };
    
    loadInitialConcepts();
  }, []); // Run once on component mount

  // Load concepts from Firebase when user logs in
  useEffect(() => {
    const loadConcepts = async () => {
      if (currentUser) {
        // Start with localStorage first (offline-first approach)
        const saved = localStorage.getItem('showerThoughtsConcepts');
        let localConcepts: AppConcept[] = [];
        
        if (saved) {
          try {
            localConcepts = JSON.parse(saved);
            console.log('📋 Found concepts in localStorage:', localConcepts.length);
            // Immediately show localStorage data to prevent disappearing
            setGeneratedConcepts(localConcepts);
          } catch (parseError) {
            console.error('❌ Error parsing localStorage data:', parseError);
          }
        }
        
        // Then try to sync with Firebase in the background
        try {
          console.log('🔄 Loading concepts for user:', currentUser.uid);
          const firebaseConcepts = await loadUserConcepts(currentUser.uid);
          console.log('✅ Loaded concepts from Firebase:', firebaseConcepts.length);
          
          // If Firebase has more recent data, use it
          if (firebaseConcepts.length >= localConcepts.length) {
            setGeneratedConcepts(firebaseConcepts);
            // Update localStorage with Firebase data
            if (firebaseConcepts.length > 0) {
              localStorage.setItem('showerThoughtsConcepts', JSON.stringify(firebaseConcepts));
              console.log('💾 Updated localStorage with Firebase data');
            }
          } else if (localConcepts.length > 0 && firebaseConcepts.length === 0) {
            // Firebase is empty but we have local data - try to migrate
            console.log('🔄 Firebase empty, attempting to migrate localStorage data...');
            const migrationSuccess = await migrateLocalStorageToFirebase(currentUser.uid);
            if (!migrationSuccess) {
              console.log('💾 Migration failed, keeping localStorage data');
              // Keep the localStorage data we already loaded
            }
          }
        } catch (error) {
          console.error('❌ Error syncing with Firebase:', error);
          // If we have localStorage data, keep using it
          if (localConcepts.length > 0) {
            console.log('💾 Using localStorage data due to Firebase failure');
            setGeneratedConcepts(localConcepts);
          } else {
            console.log('⚠️ No data available anywhere, starting with empty state');
            setGeneratedConcepts([]);
          }
        }
      } else {
        // User logged out, but keep localStorage data for when they log back in
        console.log('⚠️ User logged out, clearing React state but keeping localStorage');
        setGeneratedConcepts([]);
      }
    };
    
    loadConcepts();
  }, [currentUser]);

  const handleConceptGenerated = async (concept: AppConcept) => {
    console.log('🔄 handleConceptGenerated called with concept:', concept.id, concept.appName);
    
    // Update local state first to immediately show in gallery
    setGeneratedConcepts(prev => {
      // Avoid duplicates by checking if concept already exists
      const exists = prev.find(c => c.id === concept.id);
      if (exists) {
        console.log('⚠️ Concept already exists in local state, skipping duplicate');
        return prev;
      }
      const updated = [concept, ...prev];
      console.log('📋 Updated local concepts count:', updated.length);
      
      // Save to localStorage as immediate backup
      try {
        localStorage.setItem('showerThoughtsConcepts', JSON.stringify(updated));
        console.log('💾 Concept saved to localStorage as backup');
      } catch (localError) {
        console.error('❌ Error saving to localStorage:', localError);
      }
      
      return updated;
    });
    
    // Try to save to Firebase in the background
    if (currentUser) {
      try {
        console.log('💾 Saving concept to Firebase for user:', currentUser.uid);
        await saveConcept(concept, currentUser.uid);
        console.log('✅ Concept saved successfully to Firebase');
      } catch (error) {
        console.error('❌ Error saving concept to Firebase:', error);
        console.log('⚠️ Concept is saved locally but not in Firebase');
      }
    } else {
      console.warn('⚠️ No current user, concept saved locally only');
    }
  };

  const handleConceptsUpdate = (updatedConcepts: AppConcept[]) => {
    setGeneratedConcepts(updatedConcepts);
    
    // Always save to localStorage when concepts are updated
    try {
      localStorage.setItem('showerThoughtsConcepts', JSON.stringify(updatedConcepts));
      console.log('💾 Updated concepts saved to localStorage:', updatedConcepts.length);
    } catch (error) {
      console.error('❌ Error saving updated concepts to localStorage:', error);
    }
  };

  // Protected Route Component
  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    return currentUser ? <>{children}</> : <Navigate to="/login" replace />;
  };

  return (
    <div className="App">
      <Routes>
        {/* Landing Page - Root Route */}
        <Route 
          path="/" 
          element={
            currentUser ? 
              <Navigate to="/gallery" replace /> :
              <LandingPage 
                onGenerateIdea={() => {}}
                onViewGallery={() => {}}
                onLogin={() => {}}
                recentConcepts={generatedConcepts.slice(0, 3)}
              />
          } 
        />
        
        {/* Login Page */}
        <Route 
          path="/login" 
          element={
            currentUser ? 
              <Navigate to="/gallery" replace /> : 
              <LoginPage onBack={() => {}} />
          } 
        />
        
        
        {/* Concept Generator - Protected */}
        <Route 
          path="/generate" 
          element={
            <ProtectedRoute>
              <ConceptGenerator
                userIdea=""
                onConceptGenerated={handleConceptGenerated}
                onBack={() => {}}
              />
            </ProtectedRoute>
          } 
        />
        
        {/* Gallery - Protected */}
        <Route 
          path="/gallery" 
          element={
            <ProtectedRoute>
              <IdeaGallery
                concepts={generatedConcepts}
                onBack={() => {}}
                onGenerateNew={() => {}}
                onConceptsUpdate={handleConceptsUpdate}
              />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;